'use client';

import Image from '@/components/Image';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import { useTranslations } from '@/i18n/use-translations';

import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { postNotifyProduct } from '@/api/product';
import Wish from '@/components/Product/Wish';
import { toast } from '@/components/Toast';
import { useFrenchSummerTracking } from '../hooks/useFrenchSummerTracking';

import { EPlatform } from '@/utils/enums/platform-enum';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import appCommunicationManager from '@/utils/app-communication-manager';
import { randomFuncName } from '@/utils/tools';
import logger from '@/utils/client-logger';
// @ts-ignore
import { AlarmOutlined, CheckOutlined } from '@zeelool/icon';

// 商品数据接口
interface GoodItemProduct {
  id?: number | string;
  name?: string;
  short_name?: string;
  shop_sku?: string;
  business_sku?: string;
  image?: {
    product?: string[];
  };
  price_info?: any[];
  promo_listed_status?: number;
  [key: string]: any;
}

interface GoodItemWapProps {
  product: GoodItemProduct;
  index: number;
  tabName?: string;
  moduleName?: string;
  deviceType?: EPlatform;
  onProductClick?: (product: GoodItemProduct, index: number) => void;
  onProductExp?: (product: GoodItemProduct, index: number) => void;
}

export default function GoodItemWap({
  product,
  index,
  tabName = '',
  moduleName = '商品版位',
  deviceType = EPlatform.WEB,
  onProductClick,
  onProductExp,
}: GoodItemWapProps) {
  const currency = useStore((s) => s.currency);
  const userInfo = useStore((s) => s.userInfo);
  const summerT = useTranslations('activity.summerEdit' as any) as any;

  const [localPromoStatus, setLocalPromoStatus] = useState<number>(
    product.promo_listed_status || 3,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const setShowLoginRegisterDialog = useStore((s) => s.setShowLoginRegisterDialog);
  const setToken = useStore((s) => s.setToken);
  const refreshAccountInfo = useStore((s) => s.refreshAccountInfo);

  useEffect(() => {
    setLocalPromoStatus(product.promo_listed_status || 3);
  }, [product.promo_listed_status]);

  // 曝光检测
  const { ref: expRef, inView } = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      onProductExp?.(product, index);
    }
  }, [inView, onProductExp, product, index]);

  // 判断是否售罄或下架 (status === 2)
  const isSoldOut = product.stock <= 0;
  const isNoRestock = product.status === 2;

  const { trackNotifyMeClick, trackNotifyMeSubmit } = useFrenchSummerTracking();
  const [pendingSubscribe, setPendingSubscribe] = useState(false);

  useEffect(() => {
    // 当用户信息更新，且存在待订阅状态时，自动触发订阅
    if (userInfo?.email && pendingSubscribe) {
      const autoSubscribe = async () => {
        try {
          setIsSubmitting(true);
          await postNotifyProduct({
            email: userInfo.email,
            id: product.id as any, // 使用 id 而不是 business_sku
            notice_type: 'promo_listed',
          });
          setLocalPromoStatus(2); // 成功后更新状态为已订阅
          trackNotifyMeSubmit(product, moduleName, index, tabName);
        } catch {
          // 错误已经在 request 里处理
        } finally {
          setIsSubmitting(false);
          setPendingSubscribe(false);
        }
      };
      autoSubscribe();
    }
  }, [userInfo?.email, pendingSubscribe, product, trackNotifyMeSubmit, index, tabName, moduleName]);

  const handleNotifyMe = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    trackNotifyMeClick(product, moduleName, index, tabName);

    if (isSubmitting) return;

    if (!userInfo?.email) {
      setPendingSubscribe(true);
      if ([EPlatform.ANDROID, EPlatform.IOS].includes(deviceType)) {
        try {
          const logincallbackFnName = randomFuncName('JumpToPage');
          (window as any)[logincallbackFnName] = async (val: any) => {
            if (val !== 'ClearToken') {
              setToken(val);
              setTimeout(async () => {
                await refreshAccountInfo();
              }, 100);
            }
          };
          const loginData = {
            RouteName: '/login',
            closedCurrentPage: false,
            backToken: true,
            Callback: logincallbackFnName,
          };
          appCommunicationManager.sendMessage(EAppCommunicationFuncName.JUMP_TO_PAGE, loginData);
        } catch (error) {
          logger.error('App登录跳转失败:', error);
        }
      } else {
        setShowLoginRegisterDialog(true);
      }
      return;
    }

    try {
      setIsSubmitting(true);
      await postNotifyProduct({
        email: userInfo.email,
        id: product.id as any,
        notice_type: 'promo_listed',
      });
      setLocalPromoStatus(2);
      trackNotifyMeSubmit(product, moduleName, index, tabName);
    } catch {
      // 忽略错误
    } finally {
      setIsSubmitting(false);
    }
  };

  // 获取商品图片
  const productImg = (item: GoodItemProduct) => {
    const list = item?.image?.product || [];
    return list[0] || '';
  };

  // 处理商品点击
  const toDetail = (item: GoodItemProduct, itemIndex: number) => {
    onProductClick?.(item, itemIndex);
  };

  return (
    <div
      onClick={(e) => {
        // 如果是点击了按钮，则不需要阻止默认行为和冒泡（由按钮自身的事件处理）
        const target = e.target as HTMLElement;
        if (target.closest('button')) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        toDetail(product, index);
        // App 内部由 toDetail 中的原生通信或其它方式接管跳转，Web 端使用原生 location 跳转
        if (product.shop_sku) {
          if ([EPlatform.ANDROID, EPlatform.IOS].includes(deviceType)) {
            appCommunicationManager.sendMessage(EAppCommunicationFuncName.JUMP_TO_PAGE, {
              RouteName: `/goods-detail/${product.shop_sku}`,
              closedCurrentPage: false,
            });
          } else {
            window.location.href = `/goods-detail/${product.shop_sku}`;
          }
        }
      }}
      className="cursor-pointer"
    >
      <div
        ref={expRef}
        className="product-info bg-[#eeeeee] rounded-[4px] overflow-hidden relative w-full flex flex-col"
        style={{ aspectRatio: '182/240' }}
      >
        {/* Coming Soon Badge */}
        {(localPromoStatus === 1 || localPromoStatus === 2) && (
          <div className="absolute top-[7px] left-[7px] z-10 flex pointer-events-none rounded-[12px] bg-white/40 py-[3px] px-[5px]">
            <span className="font-bs text-[10px] text-[#282828] leading-none">
              {summerT('coomingSoon')}
            </span>
          </div>
        )}

        {/* Wishlist Icon */}
        <Wish
          data={product as any}
          className="absolute top-[10px] right-[10px] z-10 flex"
          iconSize="14px"
        />

        {/* 图片容器 - 占据主要空间 */}
        <div className="image-wrapper flex-1 px-[16px] pt-[24px] pb-[8px] flex items-center justify-center overflow-hidden">
          <Image
            src={productImg(product)}
            alt={`${product.name || ''}`}
            width={600}
            height={600}
            className="black-friday-bottom-image max-w-full max-h-full object-contain mix-blend-multiply"
          />
        </div>

        {/* 商品信息区域 - 固定底部空间 */}
        <div
          className={`product-details pt-[4px] px-[12px] ${localPromoStatus === 1 || localPromoStatus === 2 ? 'pb-[10px]' : 'pb-[16px]'} flex-shrink-0 flex flex-col items-center w-full`}
        >
          {/* 商品名称 */}
          <div className="promotion-list-item-name text-[12px] text-[#1a1c1e] mb-[4px] text-center line-clamp-1 font-br w-full">
            {product.short_name || product.name}
          </div>

          {/* 价格信息 */}
          <div className="promotion-list-item-price flex justify-center items-center h-[18px]">
            {(() => {
              const p = priceInfo(userInfo, product, currency) || {};
              const now = formatPriceNumberWithSymbol(
                String(p?.sell_price || p?.price || '0'),
                currency,
              );
              const old =
                p && p.price && p.sell_price && p.sell_price !== p.price
                  ? formatPriceNumberWithSymbol(String(p.price), currency)
                  : '';
              return isSoldOut ? (
                <span className="sold-out-price text-[14px] text-[#999999] font-bb">{now}</span>
              ) : (
                <>
                  <span className="now-price notranslate text-[14px] text-[#1a1c1e] font-bb">
                    {now}
                  </span>
                  {old && (
                    <span className="old-price notranslate text-[12px] text-[#1a1c1e] line-through ml-[4px] font-br">
                      {old}
                    </span>
                  )}
                </>
              );
            })()}
          </div>

          {/* Notify Me / Subscribed Button */}
          {(localPromoStatus === 1 || localPromoStatus === 2) && (
            <div className="w-full h-[32px] mt-[12px]">
              {localPromoStatus === 1 ? (
                <button
                  className="w-full h-full bg-white text-[#282828] font-bs text-[12px] rounded-[4px] flex items-center justify-center gap-[4px] active:opacity-80 transition-opacity"
                  onClick={handleNotifyMe}
                >
                  <AlarmOutlined className="text-[16px]" />
                  {summerT('notify')}
                </button>
              ) : (
                <button
                  className="w-full h-full bg-white text-[#282828]/50 font-bs text-[12px] rounded-[4px] flex items-center justify-center gap-[4px] cursor-not-allowed"
                  onClick={(e) => {
                    toast.info(summerT('subscribedToast'));
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <CheckOutlined className="text-[16px]" />
                  {summerT('subscribed')}
                </button>
              )}
            </div>
          )}
        </div>

        {/* 售罄/下架遮罩 (仅在 promoStatus === 3 或没有该字段时显示) */}
        {(isSoldOut || isNoRestock) &&
          (!product.promo_listed_status || product.promo_listed_status === 3) && (
            <div className="absolute inset-0 bg-[rgba(245,245,245,0.5)] z-20">
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-[65px] h-[65px] rounded-full bg-[rgba(0,0,0,0.5)] text-center text-white text-[12px] whitespace-pre-wrap leading-[1.2]">
                {isNoRestock ? 'No\nRestock' : 'Sold\nOut'}
              </span>
            </div>
          )}
      </div>
    </div>
  );
}
